import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  useReactTable, getCoreRowModel, flexRender,
} from "@tanstack/react-table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Kanban, Plus, Building2, DollarSign, Trash2, Check, LayoutGrid, Loader2, Search, ArrowUpDown, Download, X, User, Package, Calendar, AlertCircle, GitBranch, ThumbsDown, Trophy, Layers, ChevronDown, BarChart2, RefreshCw, Pencil, ChevronUp, ChevronsUpDown, Settings2, ChevronLeft, ChevronRight, TableProperties, Sparkles } from "lucide-react";
import { ICON_MAP } from "../lib/iconList";
import { useAppearance } from "../contexts/AppearanceContext";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "../components/ui/sonner";
import { usePermissions } from "../hooks/usePermissions";
import { usePreferences } from "../contexts/PreferencesContext";
import { useTablePreferences } from "../hooks/useTablePreferences";
import KanbanCard from "../components/KanbanCard";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import CustomFieldsForm from "../components/CustomFieldsForm";
import CardSelector from "../components/CardSelector";
import SearchSelect from "../components/SearchSelect";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import { formatDateTime } from "../lib/dateUtils";
import NegocioDetailModal from "../components/NegocioDetailModal";
import InsightsModal from "../components/InsightsModal";
import EmpresaFormDialog from "../components/EmpresaFormDialog";
import ContatoFormDialog from "../components/ContatoFormDialog";
import ProdutoFormDialog from "../components/ProdutoFormDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NegociosPage() {
  const { t } = useTranslation();
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState(
    () => localStorage.getItem("pipe4_selected_pipeline") || ""
  );
  const [negocios, setNegocios] = useState([]);
  const [stages, setStages] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [contatos, setContatos] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editNegocio, setEditNegocio] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailNegocio, setDetailNegocio] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [pendingModalTab, setPendingModalTab] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Update detail modal when negocios list refreshes after save
  useEffect(() => {
    if (detailOpen && detailNegocio) {
      const updated = negocios.find(n => n.id === detailNegocio.id);
      if (updated) setDetailNegocio(updated);
    }
  }, [negocios]); // eslint-disable-line
  const [viewMode, setViewMode] = useState("kanban");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("recentes");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [form, setForm] = useState({ titulo: "", empresa_id: "", contato_id: "", produto_id: "", valor_setup: "", valor_mrr: "", valor_negocio: "", estagio_id: "", tipo_negocio: "", custom_fields: {} });
  const [formLayout, setFormLayout] = useState([]);
  const [formSections, setFormSections] = useState([]);
  const [stageFieldsMap, setStageFieldsMap] = useState({});
  const [cardLayouts, setCardLayouts] = useState({});
  const [stageSlaMap, setStageSlaMap] = useState({});
  const [responsaveis, setResponsaveis] = useState([]);
  const [moduleEnabled, setModuleEnabled] = useState({ empresas: true, contatos: true, produtos: true });
  const [quickCreate, setQuickCreate] = useState(null); // "empresa" | "contato" | "produto" | null
  const [pipelinePickerOpen, setPipelinePickerOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("aberto"); // aberto | ganhos | perdidos | todos
  const [pendingMove, setPendingMove] = useState(null); // { negocioId, newStageId, negocio, missingFields }
  const [pendingMoveValues, setPendingMoveValues] = useState({});
  const [contractStageRules, setContractStageRules] = useState({});
  const [billingStageRules, setBillingStageRules] = useState({});
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [whatsappUnreadCounts, setWhatsappUnreadCounts] = useState({});
  const [taskCounts, setTaskCounts] = useState({});  // { negocio_id: { pending, overdue } }
  const [noteCounts, setNoteCounts] = useState({});  // { negocio_id: count }
  const CARDS_PER_LOAD = 50;
  const [visiblePerStage, setVisiblePerStage] = useState({});
  const appearance = useAppearance();
  const { can } = usePermissions();
  const { prefs } = usePreferences();
  const negociosRef = useRef([]);
  const lastUnreadFetch = useRef(0);
  const tc500 = appearance.colors?.["500"] || "#F97316";
  const tc50 = appearance.colors?.["50"] || "#FFF7ED";

  // Table view state
  const TABLE_PAGE_SIZE = 100;
  const { prefs: tablePrefs, updatePrefs: updateTablePrefs, loaded: tablePrefsLoaded } = useTablePreferences("negocios", { sortBy: "created_at", sortDir: "desc", columnVisibility: {} });
  const [tableData, setTableData] = useState([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [tablePage, setTablePage] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkField, setBulkField] = useState("");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkIsCustom, setBulkIsCustom] = useState(false);
  const tableSortBy = tablePrefs.sortBy;
  const tableSortDir = tablePrefs.sortDir;
  const columnVisibility = tablePrefs.columnVisibility;
  const [draggedColId, setDraggedColId] = useState(null);

  // Fetch table data (paginated mode)
  const fetchTableData = useCallback(async () => {
    if (!selectedPipelineId || viewMode !== "table") return;
    setTableLoading(true);
    try {
      const statusMap = { aberto: undefined, ganhos: "ganho", perdidos: "perdido", todos: undefined };
      const params = {
        pipeline_id: selectedPipelineId,
        skip: tablePage * TABLE_PAGE_SIZE, limit: TABLE_PAGE_SIZE,
        sort_by: tableSortBy, sort_dir: tableSortDir,
        search: searchTerm,
      };
      if (statusFilter === "ganhos") params.status_filter = "ganho";
      else if (statusFilter === "perdidos") params.status_filter = "perdido";
      else if (statusFilter === "todos") params.include_finalized = true;
      const res = await axios.get(`${API}/negocios`, { params, withCredentials: true });
      setTableData(res.data?.data || []);
      setTableTotal(res.data?.total ?? 0);
    } catch (err) { console.error(err); }
    finally { setTableLoading(false); }
  }, [selectedPipelineId, viewMode, tablePage, tableSortBy, tableSortDir, searchTerm, statusFilter]);

  useEffect(() => { if (tablePrefsLoaded && viewMode === "table") fetchTableData(); }, [fetchTableData, tablePrefsLoaded, viewMode]);
  useEffect(() => { setRowSelection({}); }, [tableData]);

  const handleTableSort = (col) => {
    if (tableSortBy === col) updateTablePrefs({ sortDir: tableSortDir === "asc" ? "desc" : "asc" });
    else updateTablePrefs({ sortBy: col, sortDir: "asc" });
    setTablePage(0);
  };

  const TableSortIcon = ({ col }) => {
    if (tableSortBy !== col) return <ChevronsUpDown className="w-3.5 h-3.5 text-slate-300" />;
    return tableSortDir === "asc" ? <ChevronUp className="w-3.5 h-3.5 text-slate-700" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-700" />;
  };

  const selectedIds = Object.keys(rowSelection).filter(k => rowSelection[k]);
  const hasSelection = selectedIds.length > 0;

  const handleBulkDelete = async () => {
    try { await axios.post(`${API}/negocios/bulk-delete`, { ids: selectedIds }, { withCredentials: true }); toast.success(`${selectedIds.length} negocio(s) excluido(s)`); setRowSelection({}); fetchTableData(); fetchAll(); } catch { toast.error("Erro ao excluir"); }
    setBulkDeleteOpen(false);
  };
  const handleBulkEdit = async () => {
    if (!bulkField || bulkValue === "") return;
    try { await axios.post(`${API}/negocios/bulk-update`, { ids: selectedIds, field: bulkField, value: bulkValue, is_custom: bulkIsCustom }, { withCredentials: true }); toast.success(`${selectedIds.length} negocio(s) atualizado(s)`); setRowSelection({}); fetchTableData(); fetchAll(); } catch { toast.error("Erro ao atualizar"); }
    setBulkEditOpen(false); setBulkField(""); setBulkValue("");
  };

  const editableFieldsTable = useMemo(() => {
    const fields = [
      { key: "titulo", label: "Titulo", isCustom: false },
      { key: "responsavel_id", label: "Responsavel", isCustom: false },
    ];
    customFields.filter(cf => !cf.deleted).forEach(cf => { fields.push({ key: cf.field_name, label: cf.field_label, isCustom: true }); });
    return fields;
  }, [customFields]);


  const fetchPipelines = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pipelines`, { withCredentials: true });
      setPipelines(res.data);
      const saved = localStorage.getItem("pipe4_selected_pipeline");
      const savedValid = saved && res.data.some(p => p.id === saved);
      if (!savedValid && res.data.length > 0) {
        // Saved pipeline no longer exists or nothing saved — use first
        setSelectedPipelineId(res.data[0].id);
        localStorage.setItem("pipe4_selected_pipeline", res.data[0].id);
      }
      // If savedValid, selectedPipelineId is already set from useState initializer
    } catch (err) { console.error(err); }
  }, []);

  const fetchAll = useCallback(async () => {
    if (!selectedPipelineId) { setLoading(false); return; }
    try {
      // Build query params based on statusFilter
      const statusParams = statusFilter === "aberto"   ? "" :
                           statusFilter === "ganhos"   ? "&include_finalized=true&status_filter=ganho" :
                           statusFilter === "perdidos" ? "&include_finalized=true&status_filter=perdido" :
                                                         "&include_finalized=true"; // todos
      const [nRes, fullRes, eRes, cRes, pRes, cfRes, layoutRes, respRes, modulesRes] = await Promise.all([
        axios.get(`${API}/negocios?pipeline_id=${selectedPipelineId}${statusParams}`, { withCredentials: true }),
        axios.get(`${API}/pipelines/${selectedPipelineId}/full`, { withCredentials: true }),
        axios.get(`${API}/empresas?limit=5000`, { withCredentials: true }),
        axios.get(`${API}/contatos?limit=5000`, { withCredentials: true }),
        axios.get(`${API}/produtos?limit=5000`, { withCredentials: true }),
        axios.get(`${API}/custom-fields?module=negocios`, { withCredentials: true }),
        axios.get(`${API}/form-layout/negocios`, { withCredentials: true }),
        axios.get(`${API}/users/responsaveis`, { withCredentials: true }),
        axios.get(`${API}/modules`, { withCredentials: true }),
      ]);
      setNegocios(nRes.data);
      negociosRef.current = nRes.data;
      setVisiblePerStage({});
      setEmpresas(eRes.data?.data || eRes.data); setContatos(cRes.data?.data || cRes.data); setProdutos(pRes.data?.data || pRes.data);
      setCustomFields(cfRes.data);
      setFormLayout(layoutRes.data.fields || []);
      setFormSections(layoutRes.data.sections || []);
      setResponsaveis(respRes.data);
      const modMap = {};
      (modulesRes.data || []).forEach(m => { modMap[m.module] = m.enabled; });
      setModuleEnabled({ empresas: modMap.empresas !== false, contatos: modMap.contatos !== false, produtos: modMap.produtos !== false });

      // Extract stage data from the /full response (no extra HTTP calls)
      const fullData = fullRes.data;
      const stagesData = fullData.stages || [];
      const sfMap = {}, clMap = {}, slaMap = {};
      stagesData.forEach(s => {
        sfMap[s.id] = s.required_fields || [];
        clMap[s.id] = s.card_layout_fields || [];
        slaMap[s.id] = s.sla || {};
      });
      // Strip internal fields before storing stages
      setStages(stagesData.map(({ required_fields, card_layout_fields, sla, ...s }) => s));
      setStageFieldsMap(sfMap);
      setCardLayouts(clMap);
      setStageSlaMap(slaMap);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [selectedPipelineId, statusFilter]);

  // Fetch contract + billing stage rules for the current pipeline
  useEffect(() => {
    if (selectedPipelineId) {
      axios.get(`${API}/autentique/pipeline-contract-rules?pipeline_id=${selectedPipelineId}`, { withCredentials: true })
        .then(r => setContractStageRules(r.data || {}))
        .catch(() => setContractStageRules({}));
      axios.get(`${API}/asaas/pipeline-billing-rules?pipeline_id=${selectedPipelineId}`, { withCredentials: true })
        .then(r => setBillingStageRules(r.data || {}))
        .catch(() => setBillingStageRules({}));
    }
  }, [selectedPipelineId]);

  useEffect(() => { fetchPipelines(); }, [fetchPipelines]);
  useEffect(() => { if (selectedPipelineId) fetchAll(); }, [selectedPipelineId, fetchAll]);

  // Deep link: /negocios?open={id}&tab=whatsapp (from notification click)
  useEffect(() => {
    const openId = searchParams.get("open");
    const tab = searchParams.get("tab");
    if (!openId) return;
    const open = async () => {
      let neg = negocios.find(n => n.id === openId);
      if (!neg) {
        try {
          const res = await axios.get(`${API}/negocios/${openId}`, { withCredentials: true });
          neg = res.data;
        } catch { /* not found */ }
      }
      if (neg) {
        setDetailNegocio(neg);
        setDetailOpen(true);
        if (tab) setPendingModalTab(tab);
      }
      setSearchParams({ open: openId }, { replace: true });
    };
    open();
  }, [searchParams]); // eslint-disable-line

  // SSE: react to real-time events
  useEffect(() => {
    const onNegocioChanged = () => { if (selectedPipelineId) fetchAll(); };
    const onWhatsappMessage = (e) => {
      if (!whatsappEnabled) return;
      // Throttle: max 1 call per 5 seconds
      const now = Date.now();
      if (now - lastUnreadFetch.current < 5000) return;
      lastUnreadFetch.current = now;
      // Update unread counts badge
      axios.get(`${API}/whatsapp/unread-counts`, { withCredentials: true })
        .then(res => setWhatsappUnreadCounts(res.data || {}))
        .catch(() => {});
      // Fire browser push notification if user enabled it
      const browserEnabled = prefs?.notification_prefs?.browser_whatsapp;
      if (browserEnabled && "Notification" in window && Notification.permission === "granted") {
        const detail = e.detail || {};
        const negocioId = detail.negocio_id;
        const negocio = negocioId ? negociosRef.current.find(n => n.id === negocioId) : null;
        new Notification("Nova mensagem WhatsApp", {
          body: negocio ? `Negócio: ${negocio.titulo}` : "Você tem uma nova mensagem",
          icon: "/favicon.ico",
        });
      }
    };
    const onTarefaChanged = () => {
      axios.get(`${API}/tasks/kanban-counts`, { withCredentials: true })
        .then(res => setTaskCounts(res.data || {})).catch(() => {});
    };
    window.addEventListener("pipe4:negocio_changed", onNegocioChanged);
    window.addEventListener("pipe4:whatsapp_message", onWhatsappMessage);
    window.addEventListener("pipe4:tarefa_changed", onTarefaChanged);
    return () => {
      window.removeEventListener("pipe4:negocio_changed", onNegocioChanged);
      window.removeEventListener("pipe4:whatsapp_message", onWhatsappMessage);
      window.removeEventListener("pipe4:tarefa_changed", onTarefaChanged);
    };
  }, [selectedPipelineId, fetchAll, whatsappEnabled, prefs]);

  // WhatsApp polling + Task counts polling
  useEffect(() => {
    axios.get(`${API}/whatsapp/status`, { withCredentials: true })
      .then(res => setWhatsappEnabled(res.data?.status !== "disabled"))
      .catch(() => setWhatsappEnabled(false));
  }, []);

  useEffect(() => {
    if (!whatsappEnabled) return;
    const fetchUnread = () => {
      axios.get(`${API}/whatsapp/unread-counts`, { withCredentials: true })
        .then(res => setWhatsappUnreadCounts(res.data || {}))
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [whatsappEnabled]);

  useEffect(() => {
    const fetchTaskCounts = () => {
      axios.get(`${API}/tasks/kanban-counts`, { withCredentials: true })
        .then(res => setTaskCounts(res.data || {}))
        .catch(() => {});
    };
    fetchTaskCounts();
    const interval = setInterval(fetchTaskCounts, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    axios.get(`${API}/notes/counts`).then(r => setNoteCounts(r.data || {})).catch(() => {});
  }, [negocios]);

  const openNew = () => { setEditNegocio(null); setForm({ titulo: "", responsavel_id: "", empresa_id: "", contato_id: "", produto_id: "", estagio_id: stages[0]?.id || "", valor_setup: "", valor_mrr: "", valor_negocio: "", tipo_negocio: appearance.tipo_negocio_padrao || "recorrente_setup", custom_fields: {} }); setDialogOpen(true); };
  const openEdit = (n) => { setEditNegocio(n); setForm({ titulo: n.titulo, responsavel_id: n.responsavel_id || "", empresa_id: n.empresa_id || "", contato_id: n.contato_id || "", produto_id: n.produto_id || "", estagio_id: n.estagio_id || "", valor_setup: n.valor_setup || "", valor_mrr: n.valor_mrr || "", valor_negocio: n.valor_negocio || "", tipo_negocio: n.tipo_negocio || appearance.tipo_negocio_padrao || "recorrente_setup", custom_fields: n.custom_fields || {} }); setDialogOpen(true); };
  const openDetail = (n) => { setDetailNegocio(n); setDetailOpen(true); setSearchParams({ open: n.id }, { replace: true }); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields from layout
    if (!form.responsavel_id) { toast.error("Selecione um Responsável."); return; }
    // Module-based required validation (replaces required_field from form layout)
    if (moduleEnabled.empresas && !form.empresa_id) { toast.error("Campo Empresa é obrigatório."); return; }
    if (moduleEnabled.contatos && !form.contato_id) { toast.error("Campo Contato é obrigatório."); return; }
    if (moduleEnabled.produtos && !form.produto_id) { toast.error("Campo Produto é obrigatório."); return; }
    // Custom field required validation
    for (const lf of formLayout) {
      if (lf.required_field && !lf.fixed) {
        const cf = customFields.find(c => c.field_name === lf.field_name);
        if (cf) { const v = form.custom_fields?.[lf.field_name]; if (!v && v !== 0) return; }
      }
    }
    // Stage 1 required fields — must be filled when creating a new negocio
    if (!editNegocio) {
      const stage1Id = stages[0]?.id;
      const stage1Required = stageFieldsMap[stage1Id] || [];
      for (const fn of stage1Required) {
        const val = form.custom_fields?.[fn];
        if (val === null || val === undefined || val === "") {
          const cf = customFields.find(c => c.field_name === fn);
          if (cf) { toast.error(`Campo obrigatório da 1ª etapa: ${cf.field_label}`); return; }
        }
      }
    }
    const tipo = form.tipo_negocio || appearance.tipo_negocio_padrao || "recorrente_setup";
    const mrr   = parseFloat(form.valor_mrr)   || 0;
    const setup = parseFloat(form.valor_setup) || 0;
    const neg   = parseFloat(form.valor_negocio) || 0;
    const payload = {
      ...form,
      pipeline_id: selectedPipelineId,
      tipo_negocio: tipo,
      valor_mrr:     tipo === "recorrente_setup" ? mrr   : 0,
      valor_setup:   tipo === "recorrente_setup" ? setup : 0,
      valor_negocio: tipo === "recorrente_setup" ? mrr + setup : tipo === "pontual" ? neg : 0,
      valor_tcv:     tipo === "recorrente_setup" ? (mrr * 12) + setup : 0,
    };
    try {
      if (editNegocio) {
        await axios.put(`${API}/negocios/${editNegocio.id}`, payload, { withCredentials: true });
        setDialogOpen(false); fetchAll();
      } else {
        const res = await axios.post(`${API}/negocios`, payload, { withCredentials: true });
        setDialogOpen(false); await fetchAll();
        if (res.data?.id) {
          const detail = await axios.get(`${API}/negocios/${res.data.id}`, { withCredentials: true });
          if (detail.data) { setDetailNegocio(detail.data); setDetailOpen(true); }
        }
      }
    } catch (err) { console.error(err); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try { await axios.delete(`${API}/negocios/${deleteTarget.id}`, { withCredentials: true }); fetchAll(); } catch (err) { console.error(err); }
    setDeleteTarget(null);
  };

  const handleDrop = async (negocioId, newStageId) => {
    const negocio = negocios.find(n => n.id === negocioId);
    if (!negocio || negocio.estagio_id === newStageId) return;

    const sourceStageIdx = stages.findIndex(s => s.id === negocio.estagio_id);
    const targetStageIdx = stages.findIndex(s => s.id === newStageId);
    const pipeline = pipelines.find(p => p.id === selectedPipelineId);

    // Rule: Proibido voltar
    if (pipeline?.proibido_voltar && targetStageIdx < sourceStageIdx) {
      toast.error("Proibido retroceder etapa neste pipeline.");
      return;
    }

    // Rule: Passagem obrigatória (apenas 1 etapa por vez)
    if (pipeline?.passagem_obrigatoria && Math.abs(targetStageIdx - sourceStageIdx) > 1) {
      const dir = targetStageIdx > sourceStageIdx ? "avançar" : "voltar";
      toast.error(`Passagem obrigatória: não é possível ${dir} mais de uma etapa por vez.`);
      return;
    }

    // Check contract rule only when ADVANCING (not when going back)
    const stageRule = contractStageRules[negocio.estagio_id];
    if (targetStageIdx > sourceStageIdx && stageRule && stageRule.rule !== "none") {
      try {
        const check = await axios.post(`${API}/autentique/check-contract-rule`, { negocio_id: negocioId }, { withCredentials: true });
        if (!check.data.allowed) {
          toast.error(check.data.message || "Regra de contrato não atendida.");
          return;
        }
      } catch { /* allow if check fails */ }
    }

    // Check billing rule only when ADVANCING
    const billingRule = billingStageRules[negocio.estagio_id];
    if (targetStageIdx > sourceStageIdx && billingRule && billingRule.rule !== "none") {
      try {
        const check = await axios.post(`${API}/asaas/check-billing-rule`, { negocio_id: negocioId }, { withCredentials: true });
        if (!check.data.allowed) {
          toast.error(check.data.message || "Regra de cobrança não atendida.");
          return;
        }
      } catch { /* allow if check fails */ }
    }

    // Check required fields of the CURRENT stage (must be filled before leaving)
    // AND required fields of the DESTINATION stage (must be filled to enter)
    const isMissing = (val) => val === null || val === undefined || val === "" || (Array.isArray(val) && val.length === 0);

    const sourceRequired = stageFieldsMap[negocio.estagio_id] || [];
    const destRequired   = stageFieldsMap[newStageId] || [];
    const allRequired    = [...new Set([...sourceRequired, ...destRequired])];

    // Native value field mapping
    const NATIVE_FIELD_MAP = {
      "__valor_setup": { label: "Setup", getValue: (n) => n.valor_setup },
      "__valor_mrr": { label: "MRR", getValue: (n) => n.valor_mrr },
      "__valor_negocio": { label: "Valor do Negócio", getValue: (n) => n.valor_negocio },
    };

    // Check native fields
    const missingNativeFields = allRequired
      .filter(fn => fn.startsWith("__") && NATIVE_FIELD_MAP[fn])
      .filter(fn => isMissing(NATIVE_FIELD_MAP[fn].getValue(negocio)))
      .map(fn => ({ field_name: fn, field_label: NATIVE_FIELD_MAP[fn].label, is_native: true }));

    // Check custom fields
    const missingCustomFields = allRequired
      .filter(fn => !fn.startsWith("__"))
      .filter(fn => isMissing(negocio.custom_fields?.[fn]))
      .map(fn => customFields.find(c => c.field_name === fn))
      .filter(Boolean);

    const missingFields = [...missingNativeFields, ...missingCustomFields];

    if (missingFields.length > 0) {
      const initialValues = {};
      missingFields.forEach(cf => {
        if (cf.is_native) {
          initialValues[cf.field_name] = NATIVE_FIELD_MAP[cf.field_name]?.getValue(negocio) || "";
        } else {
          initialValues[cf.field_name] = negocio.custom_fields?.[cf.field_name] || "";
        }
      });
      setPendingMove({ negocioId, newStageId, negocio, missingFields });
      setPendingMoveValues(initialValues);
      return;
    }
    try {
      await axios.patch(`${API}/negocios/${negocioId}/stage`, { estagio_id: newStageId }, { withCredentials: true });
      setNegocios(prev => prev.map(n => n.id === negocioId ? { ...n, estagio_id: newStageId } : n));
    } catch (err) { toast.error("Erro ao mover negócio."); console.error(err); }
  };

  const savePendingMove = async () => {
    if (!pendingMove) return;
    const { negocioId, newStageId, negocio } = pendingMove;
    // Validate ALL required fields are filled
    for (const cf of pendingMissingFields) {
      const val = pendingMoveValues[cf.field_name];
      if (val === null || val === undefined || val === "" || (Array.isArray(val) && val.length === 0)) {
        toast.error(`Preencha o campo obrigatório: ${cf.field_label}`);
        return;
      }
    }
    // Separate native and custom field values
    const nativeUpdates = {};
    const customUpdates = {};
    for (const [key, val] of Object.entries(pendingMoveValues)) {
      if (key === "__valor_setup") nativeUpdates.valor_setup = val;
      else if (key === "__valor_mrr") nativeUpdates.valor_mrr = val;
      else if (key === "__valor_negocio") nativeUpdates.valor_negocio = val;
      else customUpdates[key] = val;
    }
    const updatedCustomFields = { ...(negocio.custom_fields || {}), ...customUpdates };
    try {
      await axios.put(`${API}/negocios/${negocioId}`, {
        titulo: negocio.titulo, empresa_id: negocio.empresa_id, contato_id: negocio.contato_id,
        produto_id: negocio.produto_id, pipeline_id: negocio.pipeline_id,
        valor_mrr: nativeUpdates.valor_mrr ?? negocio.valor_mrr,
        valor_setup: nativeUpdates.valor_setup ?? negocio.valor_setup,
        valor_negocio: nativeUpdates.valor_negocio ?? negocio.valor_negocio,
        estagio_id: newStageId,
        custom_fields: updatedCustomFields,
      }, { withCredentials: true });
      setNegocios(prev => prev.map(n => n.id === negocioId ? { ...n, custom_fields: updatedCustomFields, estagio_id: newStageId, ...nativeUpdates } : n));
      setPendingMove(null); setPendingMoveValues({});
    } catch (err) { toast.error("Erro ao salvar."); console.error(err); }
  };

  const NATIVE_FIELD_MAP_REF = {
    "__valor_setup": { label: "Setup", getValue: (n) => n.valor_setup },
    "__valor_mrr": { label: "MRR", getValue: (n) => n.valor_mrr },
    "__valor_negocio": { label: "Valor do Negócio", getValue: (n) => n.valor_negocio },
  };

  const pendingMissingFields = pendingMove
    ? (() => {
        const sourceRequired = stageFieldsMap[pendingMove.negocio.estagio_id] || [];
        const destRequired   = stageFieldsMap[pendingMove.newStageId] || [];
        const allRequired    = [...new Set([...sourceRequired, ...destRequired])];
        const isMissing = (v) => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

        const nativeFields = allRequired
          .filter(fn => fn.startsWith("__") && NATIVE_FIELD_MAP_REF[fn])
          .filter(fn => isMissing(NATIVE_FIELD_MAP_REF[fn].getValue(pendingMove.negocio)))
          .map(fn => ({ field_name: fn, field_label: NATIVE_FIELD_MAP_REF[fn].label, is_native: true, field_type: "number" }));

        const customMissing = allRequired
          .filter(fn => !fn.startsWith("__"))
          .map(fn => customFields.find(c => c.field_name === fn))
          .filter(Boolean)
          .filter(cf => isMissing(pendingMove.negocio.custom_fields?.[cf.field_name]));

        return [...nativeFields, ...customMissing];
      })()
    : [];

  const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

  // Table columns
  const tableColumns = useMemo(() => {
    const TIPO_LABELS = { recorrente_setup: "Rec. Setup", recorrente_produto: "Rec. Produto", pontual: "Pontual" };
    const cols = [
      {
        id: "select", size: 40, enableSorting: false,
        header: ({ table }) => <Checkbox data-testid="select-all-checkbox" checked={table.getIsAllPageRowsSelected()} onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)} />,
        cell: ({ row }) => <Checkbox data-testid={`select-row-${row.original.id}`} checked={row.getIsSelected()} onCheckedChange={(v) => row.toggleSelected(!!v)} onClick={(e) => e.stopPropagation()} />,
      },
      {
        id: "titulo", accessorKey: "titulo", header: "Titulo", size: 220,
        cell: ({ row }) => (
          <div className="flex items-center gap-2 min-w-0" title={row.original.titulo}>
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" style={{ backgroundColor: tc500 }}>
              {row.original.titulo?.charAt(0)?.toUpperCase()}
            </div>
            <span className="font-medium text-slate-900 truncate">{row.original.titulo}</span>
          </div>
        ),
      },
    ];
    // Conditional module columns
    if (moduleEnabled.contatos) {
      cols.push({
        id: "contato_nome", header: "Contato", size: 220, enableSorting: false,
        cell: ({ row }) => {
          const n = row.original;
          if (!n.contato_nome) return <span className="text-slate-300">{"\u2014"}</span>;
          const parts = [n.contato_nome];
          if (n.contato_celular) parts.push(n.contato_celular);
          else if (n.contato_telefone) parts.push(n.contato_telefone);
          if (n.contato_email) parts.push(n.contato_email);
          const display = parts.join(" \u00b7 ");
          return <span className="text-sm text-slate-600 block truncate" title={display}>{display}</span>;
        },
      });
    }
    if (moduleEnabled.empresas) {
      cols.push({
        id: "empresa_nome", header: "Empresa", size: 150, enableSorting: false,
        cell: ({ row }) => row.original.empresa_nome
          ? <span className="text-sm text-slate-600 block truncate" title={row.original.empresa_nome}>{row.original.empresa_nome}</span>
          : <span className="text-slate-300">{"\u2014"}</span>,
      });
    }
    if (moduleEnabled.produtos) {
      cols.push({
        id: "produto_nome", header: "Produto", size: 160, enableSorting: false,
        cell: ({ row }) => {
          const nome = row.original.produto_nome;
          if (!nome) return <span className="text-slate-300">{"\u2014"}</span>;
          const tip = row.original.tipo_negocio === "recorrente_produto" && row.original.valor_negocio > 0
            ? `${nome} \u00b7 ${fmt(row.original.valor_negocio)}` : nome;
          return (
            <div className="flex items-center gap-1.5 min-w-0" title={tip}>
              <Package className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <span className="text-sm text-slate-600 truncate">{nome}</span>
            </div>
          );
        },
      });
    }
    cols.push(
      {
        id: "estagio_id", header: "Etapa", size: 140, enableSorting: false,
        cell: ({ row }) => {
          const stage = stages.find(s => s.id === row.original.estagio_id);
          if (!stage) return <span className="text-slate-300">{"\u2014"}</span>;
          return (
            <div className="flex items-center gap-1.5 min-w-0" title={stage.name}>
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
              <span className="text-sm text-slate-600 truncate">{stage.name}</span>
            </div>
          );
        },
      },
      {
        id: "tipo_negocio", header: "Tipo", size: 120, enableSorting: false,
        cell: ({ row }) => {
          const tipo = row.original.tipo_negocio;
          return tipo ? <Badge variant="outline" className="text-xs whitespace-nowrap">{TIPO_LABELS[tipo] || tipo}</Badge> : <span className="text-slate-300">{"\u2014"}</span>;
        },
      },
      {
        id: "valor_negocio", header: "Valor", size: 130, enableSorting: false,
        cell: ({ row }) => {
          const v = row.original.valor_negocio;
          return v > 0 ? <span className="text-sm font-medium text-slate-700 whitespace-nowrap" title={fmt(v)}>{fmt(v)}</span> : <span className="text-slate-300">{"\u2014"}</span>;
        },
      },
      {
        id: "valor_mrr", header: "MRR", size: 120, enableSorting: false,
        cell: ({ row }) => {
          const v = row.original.valor_mrr;
          return v > 0 ? <span className="text-sm font-medium text-slate-700 whitespace-nowrap" title={fmt(v)}>{fmt(v)}/m</span> : <span className="text-slate-300">{"\u2014"}</span>;
        },
      },
      {
        id: "valor_setup", header: "Setup", size: 120, enableSorting: false,
        cell: ({ row }) => {
          const v = row.original.valor_setup;
          return v > 0 ? <span className="text-sm font-medium text-slate-700 whitespace-nowrap" title={fmt(v)}>{fmt(v)}</span> : <span className="text-slate-300">{"\u2014"}</span>;
        },
      },
      {
        id: "valor_tcv", header: "TCV", size: 120, enableSorting: false,
        cell: ({ row }) => {
          const v = row.original.valor_tcv;
          return v > 0 ? <span className="text-sm font-medium text-slate-700 whitespace-nowrap" title={fmt(v)}>{fmt(v)}</span> : <span className="text-slate-300">{"\u2014"}</span>;
        },
      },
      {
        id: "status", header: "Status", size: 100, enableSorting: false,
        cell: ({ row }) => {
          const s = row.original.status;
          if (s === "ganho") return <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs whitespace-nowrap">Ganho</Badge>;
          if (s === "perdido") return <Badge className="bg-rose-100 text-rose-700 border-0 text-xs whitespace-nowrap">Perdido</Badge>;
          return <Badge className="bg-sky-100 text-sky-700 border-0 text-xs whitespace-nowrap">Aberto</Badge>;
        },
      },
      {
        id: "responsavel_nome", header: "Responsavel", size: 140, enableSorting: false,
        cell: ({ row }) => row.original.responsavel_nome
          ? <span className="text-sm text-slate-600 block truncate" title={row.original.responsavel_nome}>{row.original.responsavel_nome}</span>
          : <span className="text-slate-300">{"\u2014"}</span>,
      },
      {
        id: "created_at", accessorKey: "created_at", header: "Criado em", size: 140,
        cell: ({ getValue }) => <span className="text-sm text-slate-500 whitespace-nowrap">{formatDateTime(getValue())}</span>,
      },
      {
        id: "updated_at", accessorKey: "updated_at", header: "Atualizado", size: 140,
        cell: ({ getValue }) => <span className="text-sm text-slate-500 whitespace-nowrap">{formatDateTime(getValue())}</span>,
      },
    );
    // Custom fields — só exibe se obrigatório em pelo menos 1 etapa da pipeline
    const allRequiredFields = new Set();
    stages.forEach(s => (stageFieldsMap[s.id] || []).forEach(f => allRequiredFields.add(f)));
    customFields.filter(cf => !cf.deleted && allRequiredFields.has(cf.field_name)).forEach(cf => {
      cols.push({
        id: `cf_${cf.field_name}`, header: cf.field_label, size: 150, enableSorting: false,
        cell: ({ row }) => {
          const val = row.original.custom_fields?.[cf.field_name];
          if (!val && val !== 0) return <span className="text-slate-300">{"\u2014"}</span>;
          return <span className="text-sm text-slate-600 block truncate" title={String(val)}>{String(val)}</span>;
        },
      });
    });
    // Actions
    cols.push({
      id: "actions", size: 70, enableSorting: false, header: "",
      cell: ({ row }) => (
        <div className="flex items-center gap-0.5 justify-end">
          <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-sky-600" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><Pencil className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent>Abrir</TooltipContent></Tooltip>
          {can("negocios", "delete") && <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-500" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}><Trash2 className="w-3.5 h-3.5" /></Button></TooltipTrigger><TooltipContent>Excluir</TooltipContent></Tooltip>}
        </div>
      ),
    });
    return cols;
  }, [stages, customFields, tc500, can, moduleEnabled, stageFieldsMap]); // eslint-disable-line

  const tableTotalPages = Math.max(1, Math.ceil(tableTotal / TABLE_PAGE_SIZE));

  const resolvedColumnOrder = useMemo(() => {
    const saved = tablePrefs.columnOrder || [];
    if (saved.length === 0) return [];
    const allIds = tableColumns.map(c => c.id);
    const valid = saved.filter(id => allIds.includes(id));
    const missing = allIds.filter(id => !valid.includes(id));
    return [...valid, ...missing];
  }, [tablePrefs.columnOrder, tableColumns]);

  const table = useReactTable({
    data: tableData, columns: tableColumns, getCoreRowModel: getCoreRowModel(),
    manualSorting: true, manualPagination: true, pageCount: tableTotalPages,
    state: { rowSelection, columnVisibility, columnOrder: resolvedColumnOrder.length > 0 ? resolvedColumnOrder : undefined },
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: (updater) => { const next = typeof updater === "function" ? updater(columnVisibility) : updater; updateTablePrefs({ columnVisibility: next }); },
    enableRowSelection: true, getRowId: (row) => row.id,
  });

  const toggleableColumns = useMemo(() => tableColumns.filter(c => c.id !== "select" && c.id !== "actions").map(c => ({ id: c.id, label: c.header || c.id })), [tableColumns]);
  const filteredContatos = contatos;

  const getCardFieldValue = (negocio, fieldName) => {
    switch (fieldName) {
      case "titulo": return negocio.titulo;
      case "empresa": case "empresa_nome": return negocio.empresa_nome || null;
      case "contato": case "contato_nome": return negocio.contato_nome || null;
      case "produto": case "produto_nome": return negocio.produto_nome || null;
      case "valor_mrr": return negocio.valor_mrr ? `MRR ${fmt(negocio.valor_mrr)}` : null;
      case "valor_setup": return (negocio.valor_setup > 0) ? `Setup ${fmt(negocio.valor_setup)}` : null;
      case "data_previsao": return null;
      default: {
        const cf = customFields.find(c => c.field_name === fieldName);
        if (!cf) return null;
        const val = negocio.custom_fields?.[fieldName];
        if (val === null || val === undefined || val === "") return null;
        if (cf.field_type === "boolean") return val ? cf.field_label : null;
        if (cf.field_type === "select" && Array.isArray(val)) return val.join(", ");
        if (cf.field_type === "currency") return `${cf.currency_symbol || "R$"} ${val}`;
        return String(val);
      }
    }
  };

  const DEFAULT_CARD_FIELDS = [
    { field_name: "titulo", label: "Título", visible: true, bold: true, badge: false },
    { field_name: "empresa_nome", label: "Empresa", visible: true, bold: false, badge: false },
    { field_name: "valor_mrr", label: "MRR", visible: true, bold: false, badge: false },
    { field_name: "valor_setup", label: "Setup", visible: true, bold: false, badge: false },
  ];

  const getStageCardFields = (stageId) => {
    const saved = cardLayouts[stageId];
    if (saved && saved.length > 0) return saved;
    return DEFAULT_CARD_FIELDS;
  };

  const _isSlaBreached = (n, sla, nowMs) => {
    if (n.status) return false;
    const eu = new Date(n.estagio_updated_at || n.created_at).getTime();
    const days = Math.floor((nowMs - eu) / 86400000);
    return (sla.prazo_dias > 0 && days > sla.prazo_dias) || (sla.prazo_inatividade_dias > 0 && days > sla.prazo_inatividade_dias);
  };

  const handleExport = () => {
    const TIPO_LABELS = { recorrente_setup: "Rec. Setup", recorrente_produto: "Rec. Produto", pontual: "Pontual" };
    const stageMap = {}; stages.forEach(s => { stageMap[s.id] = s.name; });
    const rows = filteredNegocios.map(n => ({
      "Título": n.titulo,
      "Empresa": n.empresa_nome || "",
      "Contato": n.contato_nome || "",
      "Produto": n.produto_nome || "",
      "Etapa": stageMap[n.estagio_id] || "",
      "Tipo": TIPO_LABELS[n.tipo_negocio] || n.tipo_negocio || "",
      "Valor": n.valor_negocio || 0,
      "MRR": n.valor_mrr || 0,
      "Setup": n.valor_setup || 0,
      "TCV": n.valor_tcv || 0,
      "Status": n.status === "ganho" ? "Ganho" : n.status === "perdido" ? "Perdido" : "Aberto",
      "Responsável": n.responsavel_nome || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 15) }));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Negócios");
    XLSX.writeFile(wb, `negocios_${new Date().toISOString().slice(0, 10)}.xlsx`);
    setExportDialogOpen(false);
  };

  const filteredNegocios = negocios
    .filter(n => {
      if (!searchTerm) return true;
      const s = searchTerm.toLowerCase();
      return n.titulo?.toLowerCase().includes(s) || n.empresa_nome?.toLowerCase().includes(s) || n.contato_nome?.toLowerCase().includes(s) || n.produto_nome?.toLowerCase().includes(s) || fmt(n.valor_mrr).includes(s) || fmt(n.valor_setup).includes(s);
    })
    .sort((a, b) => {
      if (sortBy === "titulo_az") return (a.titulo || "").localeCompare(b.titulo || "");
      if (sortBy === "titulo_za") return (b.titulo || "").localeCompare(a.titulo || "");
      if (sortBy === "mrr_desc") return (b.valor_mrr || 0) - (a.valor_mrr || 0);
      if (sortBy === "mrr_asc") return (a.valor_mrr || 0) - (b.valor_mrr || 0);
      if (sortBy === "setup_desc") return (b.valor_setup || 0) - (a.valor_setup || 0);
      if (sortBy === "empresa") return (a.empresa_nome || "").localeCompare(b.empresa_nome || "");
      if (sortBy === "antigos") return (a.created_at || "").localeCompare(b.created_at || "");
      return (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || "");
    });

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-24 text-slate-400">
      <Loader2 className="w-8 h-8 animate-spin mb-3" style={{ color: tc500 }} />
      <p className="text-sm">{t("ui.carregando_pipeline")}</p>
    </div>
  );

  return (
    <div data-testid="negocios-page" className="space-y-6 flex flex-col h-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}><Kanban className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2">
              {t("negocios.title")}
              <Badge className="bg-white text-slate-500 border border-slate-300 text-sm font-medium px-3 py-1">{viewMode === "table" ? tableTotal : negocios.length}</Badge>
              {viewMode === "table" && hasSelection && <Tooltip><TooltipTrigger asChild><Badge data-testid="bulk-selection-count" className="text-white text-sm font-medium px-3 py-1 cursor-default" style={{ backgroundColor: tc500 }}>{selectedIds.length}</Badge></TooltipTrigger><TooltipContent>{selectedIds.length} negocio(s) selecionado(s)</TooltipContent></Tooltip>}
            </h1>
            <p className="text-slate-500 mt-0.5">{t("negocios.kanbanHint")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Table bulk actions */}
          {viewMode === "table" && hasSelection && (
            <>
              {can("negocios", "edit") && <Tooltip><TooltipTrigger asChild><Button data-testid="bulk-edit-btn" size="icon" className="h-9 w-9 text-white hover:opacity-90" style={{ backgroundColor: tc500 }} onClick={() => setBulkEditOpen(true)}><Pencil className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Editar campo em lote</TooltipContent></Tooltip>}
              {can("negocios", "delete") && <Tooltip><TooltipTrigger asChild><Button data-testid="bulk-delete-btn" size="icon" className="h-9 w-9 text-white bg-rose-500 hover:bg-rose-600" onClick={() => setBulkDeleteOpen(true)}><Trash2 className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Excluir selecionados</TooltipContent></Tooltip>}
              <Tooltip><TooltipTrigger asChild><Button variant="outline" size="icon" className="h-9 w-9 text-slate-600" onClick={() => setRowSelection({})}><X className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Limpar selecao</TooltipContent></Tooltip>
              <div className="w-px h-6 bg-slate-200" />
            </>
          )}
          {/* Search — first */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={searchTerm} onChange={e => { setSearchTerm(e.target.value); if (viewMode === "table") setTablePage(0); }} placeholder={t("negocios.search")} className="pl-9 w-full sm:w-48 h-9 text-sm" />
          </div>
          {/* Insights button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" onClick={() => setInsightsOpen(true)} className="h-9 w-9 p-0" style={{ backgroundColor: tc500, borderColor: tc500 }}>
                <Sparkles className="w-4 h-4 text-white" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Painel de Insights da Pipeline</TooltipContent>
          </Tooltip>
          {/* Pipeline picker button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" onClick={() => setPipelinePickerOpen(true)} className="h-9 px-3 gap-2 text-sm text-slate-700 max-w-[180px]">
                <GitBranch className="w-4 h-4 flex-shrink-0 text-slate-500" />
                <span className="truncate">{pipelines.find(p => p.id === selectedPipelineId)?.name || "Pipeline"}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Trocar pipeline</TooltipContent>
          </Tooltip>
          {viewMode !== "table" && <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" size="icon" className="h-9 w-9 text-slate-600"><ArrowUpDown className="w-4 h-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSortBy("recentes")} className={sortBy === "recentes" ? "font-semibold text-sky-700" : ""}>{t("negocios.sortRecent")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy("antigos")} className={sortBy === "antigos" ? "font-semibold text-sky-700" : ""}>{t("negocios.sortOldest")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy("titulo_az")} className={sortBy === "titulo_az" ? "font-semibold text-sky-700" : ""}>{t("negocios.sortTitleAZ")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy("titulo_za")} className={sortBy === "titulo_za" ? "font-semibold text-sky-700" : ""}>{t("negocios.sortTitleZA")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy("mrr_desc")} className={sortBy === "mrr_desc" ? "font-semibold text-sky-700" : ""}>{t("negocios.sortMrrDesc")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy("mrr_asc")} className={sortBy === "mrr_asc" ? "font-semibold text-sky-700" : ""}>{t("negocios.sortMrrAsc")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy("setup_desc")} className={sortBy === "setup_desc" ? "font-semibold text-sky-700" : ""}>{t("negocios.sortSetupDesc")}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy("empresa")} className={sortBy === "empresa" ? "font-semibold text-sky-700" : ""}>{t("negocios.sortEmpresaAZ")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>}
          {/* Column visibility (table mode only) */}
          {viewMode === "table" && (
            <Popover>
              <PopoverTrigger asChild><Button data-testid="column-visibility-btn" variant="outline" size="icon" className="h-9 w-9 text-slate-600"><Settings2 className="w-4 h-4" /></Button></PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 mb-2">Colunas visiveis</p>
                {toggleableColumns.map(col => { const isVisible = table.getColumn(col.id)?.getIsVisible() ?? true; return (<label key={col.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm text-slate-700"><Checkbox checked={isVisible} onCheckedChange={(v) => table.getColumn(col.id)?.toggleVisibility(!!v)} />{col.label}</label>); })}
              </PopoverContent>
            </Popover>
          )}
          {can("negocios", "export") && <Tooltip><TooltipTrigger asChild><Button variant="outline" size="icon" className="h-9 w-9 text-slate-600" onClick={() => setExportDialogOpen(true)}><Download className="w-4 h-4" /></Button></TooltipTrigger><TooltipContent>Exportar</TooltipContent></Tooltip>}
          {/* Single view toggle button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon"
                onClick={() => setViewMode(v => v === "kanban" ? "table" : "kanban")}
                className="h-9 w-9 border-2 transition-colors"
                style={{ borderColor: tc500, color: tc500 }}>
                {viewMode === "kanban" ? <Kanban className="w-4 h-4" /> : <TableProperties className="w-4 h-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{viewMode === "kanban" ? "Kanban → Clique para Tabela" : "Tabela → Clique para Kanban"}</TooltipContent>
          </Tooltip>
          {/* Status cycle button */}
          {(() => {
            const STATUS_CYCLE = ["aberto", "ganhos", "perdidos", "todos"];
            const STATUS_CONFIG = {
              aberto:   { icon: Layers,     label: t("negocios.open"),  borderColor: tc500,     iconColor: tc500,     next: t("negocios.won") },
              ganhos:   { icon: Trophy,     label: t("negocios.won"),   borderColor: "#10b981", iconColor: "#10b981", next: t("negocios.lost") },
              perdidos: { icon: ThumbsDown, label: t("negocios.lost"),  borderColor: "#ef4444", iconColor: "#ef4444", next: t("negocios.all") },
              todos:    { icon: LayoutGrid, label: t("negocios.all"),   borderColor: tc500,     iconColor: tc500,     next: t("negocios.open") },
            };
            const cfg = STATUS_CONFIG[statusFilter] || STATUS_CONFIG["aberto"];
            if (!cfg) return null;
            const IconC = cfg.icon;
            const nextIdx = (STATUS_CYCLE.indexOf(statusFilter) + 1) % STATUS_CYCLE.length;
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon"
                    onClick={() => setStatusFilter(STATUS_CYCLE[nextIdx])}
                    className="h-9 w-9 border-2 bg-white transition-colors hover:bg-slate-50"
                    style={{ borderColor: cfg.borderColor, color: cfg.iconColor }}>
                    <IconC className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Exibindo: <strong>{cfg.label}</strong> · Clique para ver: <strong>{cfg.next}</strong>
                </TooltipContent>
              </Tooltip>
            );
          })()}
          {can("negocios", "create") && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button data-testid="add-negocio-btn" onClick={openNew} size="icon" style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90 h-9 w-9">
                  <Plus className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("negocios.new")}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Kanban View */}
      {viewMode === "kanban" && (
      <div className="flex gap-4 overflow-x-auto pb-4 max-w-full flex-1" style={{ minHeight: 0 }}>
        {stages.map((stage) => {
          const stageNegocios = filteredNegocios.filter(n => n.estagio_id === stage.id);
          const stageMrr = stageNegocios.reduce((s, n) => s + (n.valor_mrr || 0), 0);

          // Sort: SLA breached first → WhatsApp unread → most recent
          const sla = stageSlaMap[stage.id] || {};
          const sorted = [...stageNegocios].sort((a, b) => {
            const nowMs = Date.now();
            const slaA = _isSlaBreached(a, sla, nowMs);
            const slaB = _isSlaBreached(b, sla, nowMs);
            if (slaA !== slaB) return slaA ? -1 : 1;
            const waA = whatsappUnreadCounts[a.id] || 0;
            const waB = whatsappUnreadCounts[b.id] || 0;
            if (waA !== waB) return waB - waA;
            return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
          });

          const visible = visiblePerStage[stage.id] || CARDS_PER_LOAD;
          const visibleCards = sorted.slice(0, visible);
          const hasMore = sorted.length > visible;

          return (
            <div key={stage.id} data-testid={`pipeline-column-${stage.id}`} className="flex-shrink-0 w-72 bg-slate-100/70 rounded-xl flex flex-col"
              onDragOver={e => { if (can("negocios", "mover_cards")) e.preventDefault(); }} onDrop={e => { e.preventDefault(); const nId = e.dataTransfer.getData("negocio_id"); if (nId && can("negocios", "mover_cards")) handleDrop(nId, stage.id); }}>
              <div className="p-3 border-b border-slate-200/50">
                <div className="flex items-center gap-2 mb-1"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} /><span className="text-sm font-semibold text-slate-800">{stage.name}</span><Badge className="ml-auto bg-white text-slate-600 border-0 text-xs">{stageNegocios.length}</Badge></div>
                <p className="text-xs text-slate-500">MRR: {fmt(stageMrr)}</p>
              </div>
              <div className="flex-1 p-2 space-y-2 overflow-y-auto">
                {visibleCards.map(n => {
                  const cardFields = getStageCardFields(stage.id);
                  const now = new Date();
                  const refDate = (n.status === "ganho" || n.status === "perdido") && n.data_finalizacao
                    ? new Date(n.data_finalizacao) : now;
                  const daysInStage = Math.floor((refDate - new Date(n.estagio_updated_at || n.created_at)) / 86400000);
                  const daysSinceActivity = Math.floor((refDate - new Date(n.updated_at || n.estagio_updated_at || n.created_at)) / 86400000);
                  const slaBreached_prazo = !n.status && sla.prazo_dias > 0 && daysInStage > sla.prazo_dias;
                  const slaBreached_inativo = !n.status && sla.prazo_inatividade_dias > 0 && daysSinceActivity > sla.prazo_inatividade_dias;
                  const slaBreached = slaBreached_prazo || slaBreached_inativo;
                  const slaAlerts = [
                    ...(slaBreached_prazo ? [t("sla.stageLimit")] : []),
                    ...(slaBreached_inativo ? [t("sla.inactivity")] : []),
                  ];
                  return (
                    <div key={n.id} data-testid={`negocio-card-${n.id}`}>
                      <KanbanCard
                        negocio={n}
                        cardFields={cardFields}
                        customFields={customFields}
                        canDrag={can("negocios", "mover_cards")}
                        slaBreached={slaBreached}
                        slaAlerts={slaAlerts}
                        whatsappUnread={whatsappUnreadCounts[n.id] || 0}
                        taskCount={taskCounts[n.id] || null}
                        noteCount={noteCounts[n.id] || 0}
                        tc500={tc500}
                        onClick={() => openDetail(n)}
                      />
                    </div>
                  );
                })}
                {hasMore && (
                  <div ref={el => {
                    if (!el) return;
                    const obs = new IntersectionObserver(([entry]) => {
                      if (entry.isIntersecting) {
                        setVisiblePerStage(prev => ({ ...prev, [stage.id]: (prev[stage.id] || CARDS_PER_LOAD) + CARDS_PER_LOAD }));
                        obs.disconnect();
                      }
                    }, { threshold: 0.1 });
                    obs.observe(el);
                  }} className="w-full py-3 text-center text-xs text-slate-400">
                    Carregando mais...
                  </div>
                )}
                {stageNegocios.length === 0 && <div className="text-center py-8 text-slate-300 text-xs">Nenhum negócio</div>}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Cards View */}
      {viewMode === "table" && (
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ tableLayout: 'fixed', minWidth: `${table.getVisibleFlatColumns().reduce((s, c) => s + c.getSize(), 0)}px` }}>
            <thead>
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id} className="border-b border-slate-200 bg-slate-50/80">
                  {hg.headers.map(header => { const canSort = header.column.columnDef.enableSorting !== false && header.column.columnDef.accessorKey; const isDraggable = header.id !== "select" && header.id !== "actions"; return (
                    <th key={header.id} style={{ width: header.getSize() }}
                      draggable={isDraggable}
                      onDragStart={(e) => { if (!isDraggable) return; setDraggedColId(header.id); e.dataTransfer.effectAllowed = "move"; }}
                      onDragOver={(e) => { if (isDraggable) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
                      onDrop={(e) => { e.preventDefault(); if (!draggedColId || draggedColId === header.id || !isDraggable) return; const cur = table.getState().columnOrder?.length > 0 ? [...table.getState().columnOrder] : table.getAllLeafColumns().map(c => c.id); const fi = cur.indexOf(draggedColId); const ti = cur.indexOf(header.id); if (fi !== -1 && ti !== -1) { cur.splice(fi, 1); cur.splice(ti, 0, draggedColId); updateTablePrefs({ columnOrder: cur }); } setDraggedColId(null); }}
                      onDragEnd={() => setDraggedColId(null)}
                      className={`px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap overflow-hidden text-ellipsis ${canSort ? "cursor-pointer select-none hover:text-slate-700" : ""} ${isDraggable && !canSort ? "cursor-grab" : ""} ${draggedColId === header.id ? "opacity-50" : ""}`}
                      onClick={() => canSort && handleTableSort(header.column.columnDef.accessorKey)}>
                      <div className="flex items-center gap-1.5">{flexRender(header.column.columnDef.header, header.getContext())}{canSort && <TableSortIcon col={header.column.columnDef.accessorKey} />}</div>
                    </th>); })}
                </tr>
              ))}
            </thead>
            <tbody>
              {tableLoading ? [...Array(8)].map((_, i) => (<tr key={i} className="border-b border-slate-100">{tableColumns.filter(c => table.getColumn(c.id)?.getIsVisible() !== false).map((c, j) => (<td key={j} className="px-3 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" style={{ width: `${40 + Math.random() * 40}%` }} /></td>))}</tr>))
              : table.getRowModel().rows.length === 0 ? (<tr><td colSpan={tableColumns.length} className="text-center py-16"><div className="flex flex-col items-center text-slate-400"><Kanban className="w-10 h-10 mb-3 text-slate-300" /><p className="text-sm">Nenhum negocio encontrado.</p></div></td></tr>)
              : table.getRowModel().rows.map(row => (
                <tr key={row.id} data-testid={`negocio-row-${row.original.id}`} className={`border-b border-slate-100 transition-colors cursor-pointer ${row.getIsSelected() ? "bg-slate-50" : "hover:bg-slate-50/50"}`} onClick={() => openDetail(row.original)}>
                  {row.getVisibleCells().map(cell => (<td key={cell.id} className="px-3 py-2.5 overflow-hidden whitespace-nowrap text-ellipsis">{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>))}
                </tr>))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
          <p className="text-xs text-slate-500">{tableTotal > 0 ? `${tablePage * TABLE_PAGE_SIZE + 1}-${Math.min((tablePage + 1) * TABLE_PAGE_SIZE, tableTotal)} de ${tableTotal}` : "Nenhum resultado"}</p>
          <div className="flex items-center gap-1">
            <Button data-testid="prev-page-btn" variant="outline" size="sm" className="h-8 w-8 p-0" disabled={tablePage === 0} onClick={() => setTablePage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            {[...Array(Math.min(tableTotalPages, 5))].map((_, i) => { let pn; if (tableTotalPages <= 5) pn = i; else if (tablePage < 3) pn = i; else if (tablePage > tableTotalPages - 4) pn = tableTotalPages - 5 + i; else pn = tablePage - 2 + i; return (<Button key={pn} variant={pn === tablePage ? "default" : "outline"} size="sm" className={`h-8 w-8 p-0 text-xs ${pn === tablePage ? "text-white" : ""}`} style={pn === tablePage ? { backgroundColor: tc500 } : {}} onClick={() => setTablePage(pn)}>{pn + 1}</Button>); })}
            <Button data-testid="next-page-btn" variant="outline" size="sm" className="h-8 w-8 p-0" disabled={tablePage >= tableTotalPages - 1} onClick={() => setTablePage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>
      )}

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle className="font-heading flex items-center gap-2 text-rose-600"><Trash2 className="w-5 h-5" /> Excluir {selectedIds.length} negocio(s)</AlertDialogTitle><AlertDialogDescription>Esta acao ira excluir permanentemente os negocios selecionados. Deseja continuar?</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction data-testid="confirm-bulk-delete" onClick={handleBulkDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Excluir {selectedIds.length} negocio(s)</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Pencil className="w-5 h-5" style={{ color: tc500 }} />Editar campo em lote</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">A alteracao sera aplicada a {selectedIds.length} negocio(s).</p>
            <div><Label className="text-sm text-slate-700">Campo</Label>
              <Select value={bulkField ? `${bulkIsCustom ? "cf:" : ""}${bulkField}` : "__none__"} onValueChange={(v) => { if (v === "__none__") { setBulkField(""); setBulkIsCustom(false); return; } if (v.startsWith("cf:")) { setBulkField(v.slice(3)); setBulkIsCustom(true); } else { setBulkField(v); setBulkIsCustom(false); } setBulkValue(""); }}>
                <SelectTrigger data-testid="bulk-field-select" className="mt-1"><SelectValue placeholder="Escolha o campo..." /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">Escolha o campo...</SelectItem>{editableFieldsTable.map(f => <SelectItem key={f.key} value={f.isCustom ? `cf:${f.key}` : f.key}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {bulkField && <div><Label className="text-sm text-slate-700">Novo valor</Label><Input data-testid="bulk-value-input" value={bulkValue} onChange={e => setBulkValue(e.target.value)} placeholder="Digite o novo valor..." className="mt-1" /></div>}
            <div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={() => setBulkEditOpen(false)}>Cancelar</Button><Button data-testid="confirm-bulk-edit" size="sm" disabled={!bulkField || bulkValue === ""} onClick={handleBulkEdit} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90"><Check className="w-4 h-4 mr-2" /> Aplicar</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Kanban className="w-5 h-5" style={{ color: tc500 }} />{editNegocio ? "Editar" : "Novo"} Negócio</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-3">
                {(() => {
                  const TIPO_LABELS = {
                    recorrente_setup: "Rec. com Setup",
                    recorrente_produto: "Rec. por Produto",
                    pontual: "Venda Pontual",
                  };
                  const tipoAtual = form.tipo_negocio || appearance.tipo_negocio_padrao || "recorrente_setup";

                  const fixedRenderers = {
                    titulo: () => (
                      <div key="titulo">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-slate-700">{t("negocios.titleField")}</Label>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button type="button" className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors">
                                <BarChart2 className="w-3 h-3" />
                                {TIPO_LABELS[tipoAtual]}
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {Object.entries(TIPO_LABELS).map(([val, label]) => (
                                <DropdownMenuItem key={val} onClick={() => setForm({...form, tipo_negocio: val})}
                                  className={`text-xs ${tipoAtual === val ? "font-semibold text-blue-600" : ""}`}>
                                  {tipoAtual === val && <Check className="w-3 h-3 mr-1.5 text-blue-500" />}
                                  {label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <Input data-testid="negocio-titulo-input" value={form.titulo} onChange={e => setForm({...form, titulo: e.target.value})} required />
                      </div>
                    ),
                    responsavel: () => (
                      <div key="responsavel">
                        <Label className="text-slate-700 mb-2 block">{t("ui.responsavel")}<span className="text-rose-500">*</span></Label>
                        <SearchSelect
                          items={responsaveis.map(r => ({ id: r.user_id, nome: r.name, email: r.email }))}
                          value={form.responsavel_id}
                          onChange={v => setForm({...form, responsavel_id: v})}
                          placeholder={t("negocios.selectResponsible")}
                          emptyText="Nenhum usuário disponível."
                          renderItem={(r) => (
                            <div>
                              <span className="text-sm font-medium text-slate-800">{r.nome}</span>
                              {r.email && <p className="text-xs text-slate-400">{r.email}</p>}
                            </div>
                          )}
                        />
                      </div>
                    ),
                    empresa: () => moduleEnabled.empresas ? (
                      <div key="empresa">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-slate-700">Empresa <span className="text-rose-500">*</span></Label>
                          <button type="button" onClick={() => setQuickCreate("empresa")} className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors"><Plus className="w-3 h-3" />{t("common.new")}</button>
                        </div>
                        <SearchSelect items={empresas} value={form.empresa_id} onChange={v => setForm({...form, empresa_id: v})} placeholder="Buscar empresa..." emptyText="Nenhuma empresa."
                          renderItem={(e) => (
                            <div>
                              <span className="text-sm font-medium text-slate-800">{e.nome}</span>
                            </div>
                          )} />
                      </div>
                    ) : null,
                    contato: () => moduleEnabled.contatos ? (
                      <div key="contato">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-slate-700">Contato <span className="text-rose-500">*</span></Label>
                          <button type="button" onClick={() => setQuickCreate("contato")} className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors"><Plus className="w-3 h-3" />{t("common.new")}</button>
                        </div>
                        <SearchSelect items={filteredContatos} value={form.contato_id} onChange={v => setForm({...form, contato_id: v})} placeholder="Buscar contato..." emptyText="Nenhum contato."
                          renderItem={(c) => {
                            const contacts = (c.tipos_contato || []).slice(0, 3).map(tc => tc.valor).join(" / ");
                            return (
                              <div>
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <span className="text-sm font-medium text-slate-800 truncate">{c.nome}</span>
                                </div>
                                {contacts && <p className="text-xs text-slate-400 truncate" title={contacts}>{contacts}</p>}
                              </div>
                            );
                          }} />
                      </div>
                    ) : null,
                    produto: () => moduleEnabled.produtos ? (
                      <div key="produto">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-slate-700">Produto <span className="text-rose-500">*</span></Label>
                          <button type="button" onClick={() => setQuickCreate("produto")} className="inline-flex items-center gap-1 text-xs font-medium border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-50 text-slate-600 transition-colors"><Plus className="w-3 h-3" />{t("common.new")}</button>
                        </div>
                        <SearchSelect items={produtos} value={form.produto_id} onChange={v => setForm({...form, produto_id: v})} placeholder="Buscar produto..." emptyText="Nenhum produto."
                          renderItem={(p) => (
                            <div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-sm font-medium text-slate-800">{p.nome}</span>
                                {p.sku && <span className="text-xs text-slate-400">SKU: {p.sku}</span>}
                              </div>
                              {p.preco_venda && <p className="text-xs text-slate-400">R$ {p.preco_venda}</p>}
                            </div>
                          )} />
                      </div>
                    ) : null,
                    valor_setup: () => tipoAtual === "recorrente_setup" ? <div key="valor_setup"><Label className="text-slate-700">Valor Setup (R$)</Label><Input data-testid="negocio-setup-input" type="number" step="0.01" value={form.valor_setup} onChange={e => setForm({...form, valor_setup: e.target.value})} /></div> : null,
                    valor_mrr:   () => tipoAtual === "recorrente_setup" ? <div key="valor_mrr"><Label className="text-slate-700">Valor MRR (R$)</Label><Input data-testid="negocio-mrr-input" type="number" step="0.01" value={form.valor_mrr} onChange={e => setForm({...form, valor_mrr: e.target.value})} /></div>
                      : tipoAtual === "pontual" ? <div key="valor_negocio"><Label className="text-slate-700">Valor do Negócio (R$)</Label><Input type="number" step="0.01" value={form.valor_negocio} onChange={e => setForm({...form, valor_negocio: e.target.value})} /></div>
                      : <div key="valor_produto_info" className="text-xs text-slate-400 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200"><BarChart2 className="w-3 h-3 inline mr-1" />Valor calculado automaticamente a partir do produto vinculado (qtd × preço).</div>,
                    estagio: () => <div key="estagio"><Label className="text-slate-700">Estágio</Label><Select value={form.estagio_id} onValueChange={v => setForm({...form, estagio_id: v})}><SelectTrigger data-testid="negocio-estagio-select"><SelectValue /></SelectTrigger><SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>,
                    data_previsao: () => null,
                  };
                  const orderedLayout = formLayout.length > 0 ? [...formLayout].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) : [
                    { field_name: "titulo", fixed: true, field_width: "100" },
                    { field_name: "empresa", fixed: true, field_width: "100" },
                    { field_name: "contato", fixed: true, field_width: "50" },
                    { field_name: "produto", fixed: true, field_width: "50" },
                    ...customFields.map(cf => ({ field_name: cf.field_name, fixed: false, field_width: "100" })),
                  ];
                  // Add stage-required fields that are not already in layout
                  const currentStageId = editNegocio ? editNegocio.estagio_id : form.estagio_id;
                  const stageRequiredFields = stageFieldsMap[currentStageId] || [];
                  const layoutFieldNames = new Set(orderedLayout.map(f => f.field_name));
                  // Also collect field names in sections
                  const sectionFieldNames = new Set();
                  formSections.forEach(sec => (sec.fields || []).forEach(f => sectionFieldNames.add(f.field_name)));
                  for (const fn of stageRequiredFields) {
                    if (!layoutFieldNames.has(fn) && !sectionFieldNames.has(fn)) {
                      const cf = customFields.find(c => c.field_name === fn);
                      if (cf) orderedLayout.push({ field_name: fn, fixed: false, field_width: "100" });
                    }
                  }

                  // When CREATING: show only fixed system fields + required fields for first stage
                  // When EDITING: show all layout fields (existing behaviour)
                  const isCreating = !editNegocio;
                  const firstStageRequired = new Set(stageFieldsMap[stages[0]?.id] || []);
                  const isVisible = (fieldName, isFixed) => {
                    if (!isCreating) return true;                   // edit: show all
                    if (isFixed || fixedRenderers[fieldName]) return true; // always show system fields
                    return firstStageRequired.has(fieldName);       // create: only required
                  };

                  // Filter out fields that are inside sections AND apply create-mode visibility
                  const filteredLayout = orderedLayout.filter(f =>
                    !sectionFieldNames.has(f.field_name) && isVisible(f.field_name, f.fixed)
                  );
                  // Use CSS grid col-span approach — avoids pairing issues when some fields render null
                  const renderItem = (item) => {
                    if (item.fixed && fixedRenderers[item.field_name]) return fixedRenderers[item.field_name](item);
                    if (!item.fixed) { const cf = customFields.find(c => c.field_name === item.field_name); if (cf) return <CustomFieldsForm key={cf.field_name} fields={[cf]} values={form.custom_fields} onChange={cfv => setForm({...form, custom_fields: cfv})} layout={[item]} />; }
                    return null;
                  };
                  return [
                    <div key="main-fields" className="grid grid-cols-2 gap-3">
                      {filteredLayout.map((item, i) => {
                        const rendered = renderItem(item);
                        if (!rendered) return null;
                        return <div key={i} className={item.field_width === "50" ? "col-span-1" : "col-span-2"}>{rendered}</div>;
                      })}
                    </div>,
                    ...formSections.map((sec, si) => {
                      if (!sec.fields || sec.fields.length === 0) return null;
                      // When creating: only show section if it has at least one visible required field
                      const visibleSectionFields = sec.fields.filter(sf =>
                        isVisible(sf.field_name, sf.fixed || !!fixedRenderers[sf.field_name])
                      );
                      if (isCreating && visibleSectionFields.length === 0) return null;
                      const fieldsToRender = isCreating ? visibleSectionFields : sec.fields;
                      const renderSecItem = (sf) => {
                        // Always use fixedRenderer for system fields (field may lack fixed:true if placed in section)
                        if (fixedRenderers[sf.field_name]) return fixedRenderers[sf.field_name](sf);
                        const cf = customFields.find(c => c.field_name === sf.field_name);
                        if (cf) return <CustomFieldsForm key={cf.field_name} fields={[cf]} values={form.custom_fields} onChange={cfv => setForm({...form, custom_fields: cfv})} layout={[sf]} />;
                        return null;
                      };
                      const secRows = []; let si2 = 0;
                      while (si2 < fieldsToRender.length) {
                        const sf = fieldsToRender[si2];
                        if (sf.field_width === "50" && si2 + 1 < fieldsToRender.length && fieldsToRender[si2 + 1].field_width === "50") { secRows.push([sf, fieldsToRender[si2 + 1]]); si2 += 2; }
                        else { secRows.push([sf]); si2 += 1; }
                      }
                      return (
                        <div key={`sec-${si}`}>
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">{sec.name}</p>
                          <div className="border border-slate-200 rounded-lg px-4 py-3 space-y-3">
                            {secRows.map((row, ri) => {
                              if (row.length === 2) return <div key={ri} className="grid grid-cols-2 gap-3">{row.map(r => renderSecItem(r))}</div>;
                              return renderSecItem(row[0]);
                            })}
                          </div>
                        </div>
                      );
                    })
                  ];
                })()}
              </div>
            <Button data-testid="submit-negocio-btn" type="submit" style={{ backgroundColor: tc500 }} className="w-full text-white hover:opacity-90"><Check className="w-4 h-4 mr-2" />{t("common.save")}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Entity Create Dialogs — same as in each module page */}
      <EmpresaFormDialog
        open={quickCreate === "empresa"}
        onClose={() => setQuickCreate(null)}
        onSaved={async (newItem) => {
          const res = await axios.get(`${API}/empresas?limit=5000`, { withCredentials: true });
          setEmpresas(res.data?.data || res.data);
          setForm(prev => ({ ...prev, empresa_id: newItem.id }));
          setQuickCreate(null);
        }}
      />
      <ContatoFormDialog
        open={quickCreate === "contato"}
        onClose={() => setQuickCreate(null)}
        onSaved={async (newItem) => {
          const res = await axios.get(`${API}/contatos?limit=5000`, { withCredentials: true });
          setContatos(res.data?.data || res.data);
          setForm(prev => ({ ...prev, contato_id: newItem.id }));
          setQuickCreate(null);
        }}
      />
      <ProdutoFormDialog
        open={quickCreate === "produto"}
        onClose={() => setQuickCreate(null)}
        onSaved={async (newItem) => {
          const res = await axios.get(`${API}/produtos?limit=5000`, { withCredentials: true });
          setProdutos(res.data?.data || res.data);
          setForm(prev => ({ ...prev, produto_id: newItem.id }));
          setQuickCreate(null);
        }}
      />

      {/* Pipeline Picker Modal */}
      <Dialog open={pipelinePickerOpen} onOpenChange={setPipelinePickerOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <GitBranch className="w-5 h-5" style={{ color: tc500 }} />
              {t("negocios.choosePipeline")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {pipelines.map(p => {
              const isSelected = p.id === selectedPipelineId;
              return (
                <button key={p.id} type="button"
                  onClick={() => { setSelectedPipelineId(p.id); localStorage.setItem("pipe4_selected_pipeline", p.id); setPipelinePickerOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all ${isSelected ? "text-white" : "border-slate-200 text-slate-700 hover:border-slate-400 bg-white"}`}
                  style={isSelected ? { backgroundColor: tc500, borderColor: tc500 } : undefined}>
                  <GitBranch className={`w-5 h-5 flex-shrink-0 ${isSelected ? "text-white" : "text-slate-400"}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${isSelected ? "text-white" : "text-slate-800"}`}>{p.name}</p>
                    <p className={`text-xs ${isSelected ? "text-white/70" : "text-slate-400"}`}>{p.stages_count || 0} {t("configuracoes.pipeline.stage")}{p.stages_count !== 1 ? "s" : ""}</p>
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-white flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)} title="Excluir negócio" description={`${t("common.confirm_delete")} o negócio "${deleteTarget?.titulo}"?`} onConfirm={confirmDelete} />

      {/* Pending Move Modal — fill required fields to advance */}
      <Dialog open={!!pendingMove} onOpenChange={() => { setPendingMove(null); setPendingMoveValues({}); }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Kanban className="w-5 h-5" style={{ color: tc500 }} />
              {t("negocios.fillRequiredFields")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 mb-1">
            {t("negocios.movingDeal")} <strong>{pendingMove?.negocio?.titulo}</strong> {t("negocios.moveToStage_pre")} <strong>{stages.find(s => s.id === pendingMove?.newStageId)?.name}</strong>{t("negocios.moveToStage_post")}
          </p>
          <div className="space-y-3">
            {pendingMissingFields.map(cf => cf.is_native ? (
              <div key={cf.field_name}>
                <Label className="text-sm font-medium text-slate-700">{cf.field_label} <span className="text-rose-500">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  className="mt-1"
                  placeholder="0,00"
                  value={pendingMoveValues[cf.field_name] || ""}
                  onChange={e => setPendingMoveValues(prev => ({ ...prev, [cf.field_name]: e.target.value }))}
                />
              </div>
            ) : (
              <CustomFieldsForm
                key={cf.field_name}
                fields={[{ ...cf, required: true }]}
                values={pendingMoveValues}
                onChange={vals => setPendingMoveValues(vals)}
              />
            ))}
            {pendingMissingFields.length === 0 && <p className="text-sm text-slate-400 text-center py-4">{t("negocios.allFieldsFilled")}</p>}
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => { setPendingMove(null); setPendingMoveValues({}); }}>
              <X className="w-4 h-4 mr-2" />{t("common.cancel")}
            </Button>
            <Button className="flex-1 text-white hover:opacity-90" style={{ backgroundColor: tc500 }} onClick={savePendingMove}>
              <Check className="w-4 h-4 mr-2" />{t("negocios.saveAndMove")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2"><Download className="w-5 h-5" style={{ color: tc500 }} />{t("ui.exportar_negocios")}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">{t("negocios.export_desc") || "Deseja fazer download de todas as informações da listagem em formato de planilha?"}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-slate-600"><X className="w-4 h-4 mr-2" />{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleExport} style={{ backgroundColor: tc500 }} className="text-white hover:opacity-90"><Download className="w-4 h-4 mr-2" />{t("ui.baixar_excel")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Negocio Detail Modal */}
      <NegocioDetailModal
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setDetailNegocio(null); setPendingModalTab(null); }}
        initialTab={pendingModalTab}
        negocio={detailNegocio}
        stages={stages}
        pipeline={pipelines.find(p => p.id === selectedPipelineId)}
        stageCardLayout={detailNegocio ? (cardLayouts[detailNegocio.estagio_id] || []) : []}
        stageSla={detailNegocio ? (stageSlaMap[detailNegocio.estagio_id] || {}) : {}}
        stageFieldsMap={stageFieldsMap}
        responsaveis={responsaveis}
        moduleEnabled={moduleEnabled}
        customFields={customFields}
        formLayout={formLayout}
        formSections={formSections}
        empresas={empresas}
        contatos={contatos}
        produtos={produtos}
        negocios={negocios}
        whatsappUnread={whatsappUnreadCounts[detailNegocio?.id] || 0}
        onNavigate={async (n) => {
          try {
            const res = await axios.get(`${API}/negocios/${n.id}`);
            setDetailNegocio(res.data);
            setSearchParams({ open: n.id }, { replace: true });
          } catch {
            setDetailNegocio(n);
            setSearchParams({ open: n.id }, { replace: true });
          }
        }}
        onSaved={async () => {
          fetchAll();
          // Re-fetch updated negocio directly (it may have left the filtered list)
          if (detailNegocio?.id) {
            try {
              const res = await axios.get(`${API}/negocios/${detailNegocio.id}`, { withCredentials: true });
              if (res.data) setDetailNegocio(res.data);
            } catch {}
          }
        }}
      />
      <InsightsModal
        open={insightsOpen}
        onClose={setInsightsOpen}
        pipelines={pipelines}
        initialPipelineId={selectedPipelineId}
        onOpenDeal={(dealId) => {
          const deal = negocios.find(n => n.id === dealId);
          if (deal) openDetail(deal);
        }}
      />
    </div>
  );
}
